import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import useDropdownPosition from '../../hooks/useDropdownPosition';

export type ActionMenuItem = {
    label: string;
    onClick: () => void;
    /** Shown greyed out, with `disabledReason` as the explanation. */
    disabled?: boolean;
    disabledReason?: string;
    icon?: React.ReactNode;
    /** Draws a divider above this item, to set riskier actions apart. */
    separatorBefore?: boolean;
};

function EllipsisIcon() {
    return (
        <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
        >
            <circle cx="5" cy="12" r="1.8" />
            <circle cx="12" cy="12" r="1.8" />
            <circle cx="19" cy="12" r="1.8" />
        </svg>
    );
}

/**
 * The items themselves, shared by the "…" menu and the right-click menu so
 * the two cannot drift apart.
 */
export function MenuItemList({
    items,
    onSelected,
}: {
    items: ActionMenuItem[];
    onSelected: () => void;
}) {
    return (
        <>
            {items.map((item) => (
                <React.Fragment key={item.label}>
                    {item.separatorBefore && <div className="my-1 border-t border-border" />}
                    <button
                        type="button"
                        role="menuitem"
                        disabled={item.disabled}
                        onClick={() => {
                            onSelected();
                            item.onClick();
                        }}
                        className={clsx(
                            'w-full text-left px-3 py-2 text-sm flex items-start gap-2',
                            item.disabled
                                ? 'text-foreground-muted opacity-50 cursor-not-allowed'
                                : 'text-foreground hover:bg-surface-muted cursor-pointer'
                        )}
                    >
                        {item.icon && <span className="flex-shrink-0 mt-0.5">{item.icon}</span>}
                        <span>
                            {item.label}
                            {item.disabled && item.disabledReason && (
                                <span className="block text-xs opacity-80">
                                    {item.disabledReason}
                                </span>
                            )}
                        </span>
                    </button>
                </React.Fragment>
            ))}
        </>
    );
}

/**
 * Overflow ("…") menu for row actions.
 *
 * The menu is portaled to document.body and positioned as fixed, because the
 * tables it sits in have overflow-hidden ancestors that would otherwise clip
 * it - the same reason Select does this.
 *
 * Items carry their own labels rather than relying on tooltips: hover tooltips
 * are slow to appear and unusable for discovering what an action does.
 */
export default function ActionMenu({ items, label }: { items: ActionMenuItem[]; label?: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const { triggerRef, menuRef, position } = useDropdownPosition<HTMLButtonElement>(isOpen);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setIsOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, menuRef]);

    return (
        <div ref={containerRef} className="inline-block">
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={label || 'Actions'}
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                    'inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors',
                    isOpen
                        ? 'border-orange-500 text-orange-500'
                        : 'border-border text-foreground-secondary hover:border-orange-500 hover:text-orange-500'
                )}
            >
                <EllipsisIcon />
            </button>

            {isOpen &&
                position &&
                ReactDOM.createPortal(
                    <div
                        ref={menuRef}
                        role="menu"
                        style={{
                            position: 'fixed',
                            top: position.top,
                            // Right-align the menu with the trigger so it does
                            // not run off the edge in the last column.
                            left: Math.max(8, position.left + position.width - 240),
                            width: 240,
                        }}
                        className="z-50 bg-surface border border-border rounded-lg shadow-lg py-1"
                    >
                        <MenuItemList items={items} onSelected={() => setIsOpen(false)} />
                    </div>,
                    document.body
                )}
        </div>
    );
}
